import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuthStore } from "../../stores/authStore";
import { ADMIN_LINKS, TECH_LINKS, PARTNER_LINKS } from "../../config/panelLinks";

const LINKS_BY_VARIANT = {
  admin: ADMIN_LINKS,
  technician: TECH_LINKS,
  partner: PARTNER_LINKS,
};

export default function Sidebar({ variant = "admin" }) {
  const modules = useAuthStore((s) => s.modules) || [];
  // A link with no moduleKey is always visible (e.g. Technician's fixed
  // panel, or Dashboard/Profile in Admin/Partner panels); a link that
  // declares one is gated by the logged-in user's modules — admin's
  // per-role modules or a channel partner's enabledModules alike.
  const links = (LINKS_BY_VARIANT[variant] || TECH_LINKS).filter(
    (l) => !l.moduleKey || modules.includes(l.moduleKey),
  );
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navContent = (
    <>
      <div className="p-4 border-b border-neutral-800">
        {/* <img
          src="/dm_logo-updated.png"
          alt="MechOnGo Automobiles"
          className="h-20 w-auto"
        /> */}
        <p className="text-xs text-neutral-500 mt-1 capitalize">
          {variant} Panel
        </p>
      </div>

      <nav className="flex-1 py-4 overflow-auto">
        {links.map(({ label, to, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "text-white bg-primary-600/20 border-r-2 border-primary-500"
                  : "text-neutral-400 hover:text-white hover:bg-neutral-800"
              }`
            }
          >
            <Icon size={18} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-neutral-800">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-neutral-400 hover:text-white hover:bg-neutral-800 w-full rounded transition-colors"
        >
          <LogOut size={18} />
          <span>Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-neutral-900 text-white flex items-center justify-between px-4 h-14">
        <img
          src="/dm_logo-2-updated.png"
          alt="MechOnGo Automobiles"
          className="h-12 w-auto"
        />
        <button onClick={() => setOpen(!open)} className="p-2">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Mobile slide-out sidebar */}
      <aside
        className={`md:hidden fixed top-14 left-0 bottom-0 z-30 w-64 bg-neutral-900 text-neutral-300 transform transition-transform ${
          open ? "translate-x-0" : "-translate-x-full"
        } flex flex-col`}
      >
        {navContent}
      </aside>

      {/* Mobile spacer */}
      <div className="md:hidden h-14 shrink-0" />

      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-60 bg-neutral-900 text-neutral-300 min-h-screen shrink-0">
        {navContent}
      </aside>
    </>
  );
}
